import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import Dashboard from "./pages/Dashboard";
import Subscribers from "./pages/Subscribers";
import AdminSubscribers from "./pages/AdminSubscribers";
import ContentGenerator from "./pages/ContentGenerator";
import Assessments from "./pages/Assessments";
import StudentProfile from "./pages/StudentProfile";
import StudentLearn from "./pages/StudentLearn";
import AssessmentHistory from "./pages/AssessmentHistory";
import Leaderboard from "./pages/Leaderboard";
import PracticeTests from "./pages/PracticeTests";
import StudyPlans from "./pages/StudyPlans";
import Certificates from "./pages/Certificates";
import AdminLayout from "./components/AdminLayout";
import AdminParents from "./pages/AdminParents";
import AdminStudents from "./pages/AdminStudents";
import AdminTeachers from "./pages/AdminTeachers";
import AdminSchools from "./pages/AdminSchools";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminConsole from "./pages/AdminConsole";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminInvitePage from "./pages/AdminInvitePage";
import ContentManagerPortal from "./pages/ContentManagerPortal";
import ContentManagerEditor from "./pages/ContentManagerEditor";
import RolePortalLoginPage from "./pages/RolePortalLoginPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import LandingPage from "./pages/LandingPage";
import UniversalLogin from "./pages/UniversalLogin";
import ParentDashboard from "./pages/ParentDashboard";
import SalesPresentation from "./pages/SalesPresentation";
import WhyEduzy from "./pages/WhyEduzy";
import EduzyPresentation from "./pages/EduzyPresentation";
import Checkout from "./pages/Checkout";
import FAQ from "./pages/FAQ";
import RegistrationGuide from "./pages/RegistrationGuide";
// School Portal
import SchoolLogin from "./pages/SchoolLogin";
import SchoolRegister from "./pages/SchoolRegister";
import SchoolLayout from "./components/SchoolLayout";
import SchoolDashboard from "./pages/SchoolDashboard";
import SchoolStudents from "./pages/SchoolStudents";
import SchoolTeachers from "./pages/SchoolTeachers";
import SchoolBilling from "./pages/SchoolBilling";
import SchoolAnalytics from "./pages/SchoolAnalytics";
import SchoolSettings from "./pages/SchoolSettings";
import SchoolAnnouncements from "./pages/SchoolAnnouncements";
import SchoolsLanding from "./pages/SchoolsLanding";
import AdminSchoolManagement from "./pages/AdminSchoolManagement";
import AdminFinanceDashboard from "./pages/AdminFinanceDashboard";
import AdminWhatsAppLogs from "./pages/AdminWhatsAppLogs";
import QuestionBankAdmin from "./pages/QuestionBankAdmin";
// Teacher Portal
import TeacherLogin from "./pages/TeacherLogin";
import TeacherLayout from "./pages/TeacherLayout";
import TeacherDashboard from "./pages/TeacherDashboard";
import TeacherClasses from "./pages/TeacherClasses";
import TeacherStudents from "./pages/TeacherStudents";
import TeacherLeaderboard from "./pages/TeacherLeaderboard";
import TeacherInsights from "./pages/TeacherInsights";
import TeacherMessages from "./pages/TeacherMessages";
import TeacherBroadcast from "./pages/TeacherBroadcast";
// Parent Messages
import ParentMessages from "./pages/ParentMessages";
// Sales Portal
import SalesLogin from "./pages/SalesLogin";
import SalesLayout from "./pages/SalesLayout";
import SalesDashboard from "./pages/SalesDashboard";
import SalesPricingCalculator from "./pages/SalesPricingCalculator";
import SalesQuotes from "./pages/SalesQuotes";
import SalesSchools from "./pages/SalesSchools";
import SalesLeads from "./pages/SalesLeads";
import ConsentPreview from "./pages/ConsentPreview";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ReactivatePage from "./pages/ReactivatePage";
import LearningShop from "./pages/LearningShop";
import PurchaseHistory from "./pages/PurchaseHistory";
import EduAssist from "./pages/EduAssist";
import EduWise from "./pages/EduWise";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import { CartProvider } from "@/contexts/CartContext";
import CartDrawer from "@/components/shop/CartDrawer";
import { installReadOnlyInterceptor } from "@/components/admin/EnvContext";

installReadOnlyInterceptor();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Google Analytics page view tracking for SPA
function usePageTracking() {
  const location = useLocation();
  
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-BW8Z7KDS03', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
}

// Wrapper component for page tracking
function AppRoutes() {
  usePageTracking();
  
  return (
    <Routes>
          {/* Landing Page - Default Route */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Login Page (Universal Login for Student/Parent/Admin) */}
          <Route path="/login" element={<UniversalLogin />} />
          
          {/* Sales/Marketing Page */}
          <Route path="/why-eduzy" element={<WhyEduzy />} />
          <Route path="/presentation" element={<SalesPresentation />} />
          <Route path="/story" element={<WhyEduzy />} />
          <Route path="/download" element={<EduzyPresentation />} />
          <Route path="/slides" element={<EduzyPresentation />} />
          <Route path="/how-to-register" element={<RegistrationGuide />} />
          <Route path="/registration-guide" element={<RegistrationGuide />} />
          <Route path="/consent-preview" element={<ConsentPreview />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/reactivate" element={<ReactivatePage />} />
          <Route path="/eduquest" element={<LearningShop />} />
          <Route path="/purchases" element={<PurchaseHistory />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/eduassist" element={<EduAssist />} />
          <Route path="/eduwise" element={<EduWise />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          
          {/* Pricing Page - Redirect to EduWise */}
          <Route path="/pricing" element={<Navigate to="/eduwise" replace />} />
          
          {/* Student Portal Routes */}
          <Route path="/student/login" element={<Navigate to="/login" replace />} />
          <Route path="/student/profile" element={<StudentProfile />} />
          <Route path="/student/learn" element={<StudentLearn />} />
          <Route path="/student/history" element={<AssessmentHistory />} />
          <Route path="/student/leaderboard" element={<Leaderboard />} />
          <Route path="/student/practice-tests" element={<PracticeTests />} />
          <Route path="/student/study-plans" element={<StudyPlans />} />
          <Route path="/student/certificates" element={<Certificates />} />
          
          {/* Parent Portal Routes */}
          <Route path="/parent" element={<Navigate to="/login" replace />} />
          <Route path="/parent/dashboard" element={<ParentDashboard />} />
          <Route path="/parent/messages" element={<ParentMessages />} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/cancellation" element={<RefundPolicy />} />
          <Route path="/faq" element={<FAQ />} />
          
          {/* Content Manager Portal (public activation + login) */}
          <Route path="/contentmanager" element={<ContentManagerPortal />} />
          <Route path="/contentmanager/editor" element={<ContentManagerEditor />} />

          {/* Role-locked staff login portals */}
          <Route path="/support" element={<RolePortalLoginPage portalKey="support" />} />
          <Route path="/finance" element={<RolePortalLoginPage portalKey="finance" />} />
          <Route path="/analyst" element={<RolePortalLoginPage portalKey="analyst" />} />

          {/* Admin Portal Routes */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/invite/:token" element={<AdminInvitePage />} />
          <Route path="/admin/dashboard" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
          </Route>
          <Route path="/admin/parents" element={<AdminLayout />}>
            <Route index element={<AdminParents />} />
          </Route>
          <Route path="/admin/students" element={<AdminLayout />}>
            <Route index element={<AdminStudents />} />
          </Route>
          <Route path="/admin/teachers" element={<AdminLayout />}>
            <Route index element={<AdminTeachers />} />
          </Route>
          <Route path="/admin/schools" element={<AdminLayout />}>
            <Route index element={<AdminSchools />} />
          </Route>
          <Route path="/admin/subscriptions" element={<AdminLayout />}>
            <Route index element={<AdminSubscriptions />} />
          </Route>
          <Route path="/admin/finance" element={<AdminLayout />}>
            <Route index element={<AdminFinanceDashboard />} />
          </Route>
          
          {/* Admin section pages (formerly /admin/console — now clean per-section URLs) */}
          <Route element={<AdminLayout />}>
            <Route path="/admin/admins" element={<AdminConsole />} />
            <Route path="/admin/roles" element={<AdminConsole />} />
            <Route path="/admin/tickets" element={<AdminConsole />} />
            <Route path="/admin/settings" element={<AdminConsole />} />
            <Route path="/admin/audit" element={<AdminConsole />} />
            <Route path="/admin/emails" element={<AdminConsole />} />
            <Route path="/admin/curriculum" element={<AdminConsole />} />
            <Route path="/admin/content-editor" element={<AdminConsole />} />
            <Route path="/admin/migration" element={<AdminConsole />} />
            <Route path="/admin/records" element={<AdminConsole />} />
          </Route>
          <Route path="/admin/question-bank" element={<AdminLayout />}>
            <Route index element={<QuestionBankAdmin />} />
          </Route>
          <Route path="/admin/whatsapp-logs" element={<AdminLayout />}>
            <Route index element={<AdminWhatsAppLogs />} />
          </Route>
          
          {/* School Portal Routes */}
          <Route path="/edusphere" element={<SchoolsLanding />} />
          <Route path="/schools" element={<Navigate to="/edusphere" replace />} />
          <Route path="/school/login" element={<SchoolLogin />} />
          <Route path="/school/register" element={<SchoolRegister />} />
          <Route path="/school/dashboard" element={<SchoolLayout />}>
            <Route index element={<SchoolDashboard />} />
          </Route>
          <Route path="/school/students" element={<SchoolLayout />}>
            <Route index element={<SchoolStudents />} />
          </Route>
          <Route path="/school/students/upload" element={<SchoolLayout />}>
            <Route index element={<SchoolStudents />} />
          </Route>
          <Route path="/school/teachers" element={<SchoolLayout />}>
            <Route index element={<SchoolTeachers />} />
          </Route>
          <Route path="/school/billing" element={<SchoolLayout />}>
            <Route index element={<SchoolBilling />} />
          </Route>
          <Route path="/school/analytics" element={<SchoolLayout />}>
            <Route index element={<SchoolAnalytics />} />
          </Route>
          <Route path="/school/settings" element={<SchoolLayout />}>
            <Route index element={<SchoolSettings />} />
          </Route>
          <Route path="/school/announcements" element={<SchoolLayout />}>
            <Route index element={<SchoolAnnouncements />} />
          </Route>
          
          {/* Teacher Portal Routes */}
          <Route path="/teacher/login" element={<TeacherLogin />} />
          <Route path="/teacher/dashboard" element={<TeacherLayout />}>
            <Route index element={<TeacherDashboard />} />
          </Route>
          <Route path="/teacher/classes" element={<TeacherLayout />}>
            <Route index element={<TeacherClasses />} />
          </Route>
          <Route path="/teacher/students" element={<TeacherLayout />}>
            <Route index element={<TeacherStudents />} />
          </Route>
          <Route path="/teacher/leaderboard" element={<TeacherLayout />}>
            <Route index element={<TeacherLeaderboard />} />
          </Route>
          <Route path="/teacher/insights" element={<TeacherLayout />}>
            <Route index element={<TeacherInsights />} />
          </Route>
          <Route path="/teacher/messages" element={<TeacherLayout />}>
            <Route index element={<TeacherMessages />} />
          </Route>
          <Route path="/teacher/broadcast" element={<TeacherLayout />}>
            <Route index element={<TeacherBroadcast />} />
          </Route>
          
          {/* Sales Portal Routes */}
          <Route path="/sales/login" element={<SalesLogin />} />
          <Route path="/sales/dashboard" element={<SalesLayout />}>
            <Route index element={<SalesDashboard />} />
          </Route>
          <Route path="/sales/calculator" element={<SalesLayout />}>
            <Route index element={<SalesPricingCalculator />} />
          </Route>
          <Route path="/sales/quotes" element={<SalesLayout />}>
            <Route index element={<SalesQuotes />} />
          </Route>
          <Route path="/sales/schools" element={<SalesLayout />}>
            <Route index element={<SalesSchools />} />
          </Route>
          <Route path="/sales/leads" element={<SalesLayout />}>
            <Route index element={<SalesLeads />} />
          </Route>
          
          {/* Redirect old routes */}
          <Route path="/subscribers" element={<Navigate to="/admin/subscribers" replace />} />
          <Route path="/content" element={<Navigate to="/admin/content" replace />} />
          <Route path="/assessments" element={<Navigate to="/admin/assessments" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <div className="App">
          <BrowserRouter>
            <AppRoutes />
            <CartDrawer />
            <CookieConsent />
          </BrowserRouter>
          <Toaster />
        </div>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
